// src/pages/Menu/Articulacion/Documents/ProjectFormModal.jsx
import React, { useMemo } from "react";
import { ArrowLeft, Edit, X, Plus, Trash2, MapPin, Info, Landmark, Briefcase } from "lucide-react";
import { regionsData } from "../../../../../constants/regiones";
import TooltipHelper from "../../../../../components/common/TooltipHelper";

/* Segmented / Toggle bonito (igual que AddEntityModal) */
const Segmented = ({ value, onChange, options, ariaLabel }) => (
  <div
    role="tablist"
    aria-label={ariaLabel}
    className="inline-flex flex-wrap gap-1 rounded-2xl border border-gray-200 bg-white p-1"
  >
    {options.map((opt) => {
      const active = value === opt.value;
      return (
        <button
          key={opt.value}
          type="button"
          role="tab"
          aria-selected={active}
          onClick={() => onChange(opt.value)}
          className={[
            "inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm transition",
            active
              ? "bg-gradient-to-r from-[#0c3b66] to-[#0488AF] text-white shadow"
              : "text-gray-700 hover:bg-gray-50",
          ].join(" ")}
          title={opt.label}
        >
          {opt.icon}
          <span>{opt.label}</span>
        </button>
      );
    })}
  </div>
);

export default function ProjectFormModal({
  open,
  mode = "edit", // "create" | "edit"
  form,
  setForm,
  catKeys,
  catLabels,
  ALL_STATUSES,
  STATUS_LABELS = {},
  inputClass,
  pretty,
  saving,
  onClose,
  onSave,
}) {
  if (!open) return null;

  const isCreate = mode === "create";

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden />
      <div className="absolute inset-0 grid place-items-center p-6">
        <div
          className="rounded-2xl p-[2px]"
          style={{ background: "linear-gradient(135deg,#E6F0FF 0%,#E6FBFF 100%)" }}
        >
          <div className="w-[min(92vw,52rem)] xl:w-[min(88vw,56rem)] rounded-2xl overflow-hidden bg-white ring-1 ring-black/5 flex flex-col max-h-[90vh]">
            {/* Header */}
            <div
              className="px-5 py-4 text-white"
              style={{ background: "linear-gradient(90deg,#0c3b66,#0488AF)" }}
            >
              <div className="flex items-center justify-between">
                <div className="font-semibold text-base sm:text-lg break-words">
                  {isCreate ? "Crear proyecto" : "Editar proyecto"}
                </div>
                <button
                  className="p-2 rounded-lg hover:bg-white/10"
                  onClick={onClose}
                  aria-label="Cerrar"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Body scrollable */}
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Título del proyecto
                </label>
                <input
                  className={`${inputClass} w-full mt-1`}
                  placeholder="Título del proyecto"
                  value={form.title}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Categoría (opcional)
                  </label>
                  <select
                    className={`${inputClass} w-full mt-1`}
                    value={form.category}
                    onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                  >
                    <option value="">Sin categoría</option>
                    {catKeys.map((c) => (
                      <option key={c} value={c}>
                        {catLabels[c] || pretty(c)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Estado
                  </label>
                  <select
                    className={`${inputClass} w-full mt-1`}
                    value={form.status}
                    onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                  >
                    {ALL_STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {STATUS_LABELS[s] || pretty(s)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                  Ámbito
                  <TooltipHelper text="Indica si el proyecto es ejecutado por una entidad pública o privada." />
                </label>
                <Segmented
                  ariaLabel="Ámbito"
                  value={form.isPublic === true ? true : form.isPublic === false ? false : undefined}
                  onChange={(v) => setForm((f) => ({ 
                    ...f, 
                    isPublic: v === true ? true : v === false ? false : undefined 
                  }))}
                  options={[
                    { value: true, label: "Público", icon: <Landmark size={16} /> },
                    { value: false, label: "Privado", icon: <Briefcase size={16} /> },
                  ]}
                />
              </div>

              {/* Múltiples ubicaciones (región/provincia/comuna) */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                    Ubicaciones (región/provincia/comuna)
                    <TooltipHelper text="Puedes agregar múltiples ubicaciones. Primero selecciona la región, luego la provincia y finalmente la comuna." />
                  </label>
                  <button
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, locations: [...(f.locations || []), { region: "", province: "", district: "" }] }))}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-r from-[#09274A] to-[#0488AF] text-white text-sm font-semibold shadow hover:from-[#071f3d] hover:to-[#03779C] transition-colors"
                  >
                    <Plus size={14} /> Agregar ubicación
                  </button>
                </div>
                {(form.locations || []).length === 0 ? (
                  <p className="text-xs text-gray-500 italic">No hay ubicaciones agregadas</p>
                ) : (
                  <div className="space-y-3">
                    {(form.locations || []).map((loc, idx) => (
                      <LocationSelector
                        key={idx}
                        location={loc}
                        index={idx}
                        form={form}
                        setForm={setForm}
                        inputClass={inputClass}
                      />
                    ))}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium text-gray-700 flex items-center gap-1">
                      Fecha de inicio (opcional)
                      <TooltipHelper text='Activa "Solo mes" si solo conoces el mes y año, sin el día específico. Se guardará como el primer día del mes.' />
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.startDateMonthOnly}
                        onChange={(e) => {
                          setForm((f) => ({ 
                            ...f, 
                            startDateMonthOnly: e.target.checked,
                            startDate: e.target.checked ? "" : f.startDate
                          }));
                        }}
                        className="w-4 h-4 rounded border-gray-300 text-[#0488AF] focus:ring-[#0488AF]"
                      />
                      <span className="text-xs text-gray-600">Solo mes</span>
                    </label>
                  </div>
                  {form.startDateMonthOnly ? (
                    <input
                      type="month"
                      className={`${inputClass} w-full`}
                      value={form.startDate ? form.startDate.slice(0, 7) : ""}
                      onChange={(e) => setForm((f) => ({ ...f, startDate: e.target.value ? `${e.target.value}-01` : "" }))}
                    />
                  ) : (
                    <input
                      type="date"
                      className={`${inputClass} w-full`}
                      value={form.startDate}
                      onChange={(e) => setForm((f) => ({ ...f, startDate: e.target.value }))}
                    />
                  )}
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium text-gray-700 flex items-center gap-1">
                      Fecha de fin (opcional)
                      <TooltipHelper text='Activa "Solo mes" si solo conoces el mes y año, sin el día específico. Se guardará como el primer día del mes.' />
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.endDateMonthOnly}
                        onChange={(e) => {
                          setForm((f) => ({ 
                            ...f, 
                            endDateMonthOnly: e.target.checked,
                            endDate: e.target.checked ? "" : f.endDate
                          }));
                        }}
                        className="w-4 h-4 rounded border-gray-300 text-[#0488AF] focus:ring-[#0488AF]"
                      />
                      <span className="text-xs text-gray-600">Solo mes</span>
                    </label>
                  </div>
                  {form.endDateMonthOnly ? (
                    <input
                      type="month"
                      className={`${inputClass} w-full`}
                      value={form.endDate ? form.endDate.slice(0, 7) : ""}
                      onChange={(e) => setForm((f) => ({ ...f, endDate: e.target.value ? `${e.target.value}-01` : "" }))}
                    />
                  ) : (
                    <input
                      type="date"
                      className={`${inputClass} w-full`}
                      value={form.endDate}
                      onChange={(e) => setForm((f) => ({ ...f, endDate: e.target.value }))}
                    />
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                  Etiquetas
                  <TooltipHelper text="Escribe una etiqueta y presiona Enter o escribe una coma para agregarla. Las etiquetas se convierten automáticamente a mayúsculas." />
                </label>
                <div className="rounded-xl border border-gray-200 p-2 max-h-[200px] overflow-y-auto bg-white">
                  <div className="flex flex-wrap gap-2">
                    {form.tags.map((v, i) => (
                      <span key={`${v}-${i}`} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-slate-50 text-slate-800 border border-slate-200 text-xs">
                        <span className="max-w-[10rem] truncate">{v}</span>
                        <button
                          type="button"
                          className="px-1 rounded hover:bg-slate-200 transition-colors"
                          onClick={() => setForm((f) => ({ ...f, tags: f.tags.filter((_, j) => j !== i) }))}
                          aria-label="Eliminar etiqueta"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                    <input
                      className="flex-1 min-w-[140px] outline-none px-2 py-1 text-sm bg-transparent"
                      placeholder={form.tags.length ? "" : "Etiquetas… (Enter o coma)"}
                      onKeyDown={(e) => {
                        const v = e.target.value.trim().toUpperCase();
                        if ((e.key === "Enter" || e.key === ",") && v && !form.tags.some((x) => x === v)) {
                          e.preventDefault();
                          setForm((f) => ({ ...f, tags: [...f.tags, v] }));
                          e.target.value = "";
                        }
                      }}
                    />
                  </div>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700">
                    Descripción
                  </label>
                  <span className={`text-xs ${(form.description || '').length > 400 ? 'text-red-600 font-semibold' : 'text-gray-500'}`}>
                    {(form.description || '').length}/400
                  </span>
                </div>
                <textarea
                  className={`${inputClass} w-full min-h-[100px] whitespace-pre-wrap break-words`}
                  placeholder="Descripción del proyecto"
                  value={form.description}
                  maxLength={400}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value.length <= 400) {
                      setForm((f) => ({ ...f, description: value }));
                    }
                  }}
                />
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 bg-white flex justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="h-11 px-4 rounded-xl bg-[#C42251] text-white hover:bg-[#A01D45] text-sm font-semibold transition-colors"
              >
                Cancelar
              </button>
              <button
                disabled={saving}
                onClick={onSave}
                className={`h-11 px-4 rounded-xl text-white text-sm font-semibold transition-colors ${
                  saving ? "bg-gray-400 cursor-not-allowed" : "bg-emerald-600 hover:bg-emerald-700"
                }`}
              >
                {isCreate ? "Crear proyecto" : "Guardar cambios"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* Componente para seleccionar región/provincia/comuna */
function LocationSelector({ location, index, form, setForm, inputClass }) {
  const REGIONS = regionsData?.Regions || [];

  const provinces = useMemo(() => {
    if (!location.region) return [];
    const region = REGIONS.find((r) => r.name === location.region);
    return region?.provinces || [];
  }, [location.region, REGIONS]);

  const districts = useMemo(() => {
    if (!location.province || provinces.length === 0) return [];
    const province = provinces.find((p) => p.name === location.province);
    return province?.districts || [];
  }, [location.province, provinces]);

  const updateLocation = (field, value) => {
    const newLocations = [...(form.locations || [])];
    newLocations[index] = { ...newLocations[index], [field]: value };
    // Si cambia la región, limpiar provincia y comuna
    if (field === "region") {
      newLocations[index].province = "";
      newLocations[index].district = "";
    }
    // Si cambia la provincia, limpiar comuna
    if (field === "province") {
      newLocations[index].district = "";
    }
    setForm((f) => ({ ...f, locations: newLocations }));
  };

  const removeLocation = () => {
    const newLocations = (form.locations || []).filter((_, i) => i !== index);
    setForm((f) => ({ ...f, locations: newLocations }));
  };

  return (
    <div className="p-3 rounded-xl border border-gray-200 bg-gray-50/50 space-y-2">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
          <MapPin size={16} className="text-[#0488AF]" />
          Ubicación {index + 1}
        </div>
        <button
          type="button"
          onClick={removeLocation}
          className="p-1.5 rounded-lg hover:bg-red-50 text-red-600 transition-colors"
          title="Eliminar ubicación"
          aria-label="Eliminar ubicación"
        >
          <Trash2 size={14} />
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Región</label>
          <select
            className={`${inputClass} w-full text-sm`}
            value={location.region}
            onChange={(e) => updateLocation("region", e.target.value)}
          >
            <option value="">Seleccionar región</option>
            {REGIONS.map((r) => (
              <option key={r.name} value={r.name}>
                {r.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Provincia</label>
          <select
            className={`${inputClass} w-full text-sm ${!location.region ? "opacity-50 cursor-not-allowed" : ""}`}
            value={location.province}
            onChange={(e) => updateLocation("province", e.target.value)}
            disabled={!location.region}
          >
            <option value="">Seleccionar provincia</option>
            {provinces.map((p) => (
              <option key={p.name} value={p.name}>
                {p.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Comuna</label>
          <select
            className={`${inputClass} w-full text-sm ${!location.province ? "opacity-50 cursor-not-allowed" : ""}`}
            value={location.district}
            onChange={(e) => updateLocation("district", e.target.value)}
            disabled={!location.province}
          >
            <option value="">Seleccionar comuna</option>
            {districts.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}
